/**
 * IndexedDB Tech Repository Implementation
 *
 * Implements ITechRepository using Dexie.js for IndexedDB storage.
 */

import { db } from '../../db';
import type { ITechRepository } from '../interfaces/ITechRepository';
import type { Tech, TechTier, Region, Location, TechWorkStatus } from '../../../types/ticket';
import { TechWorkStatus as TechWorkStatusEnum } from '../../../types/ticket';
import type { CreateTechDTO, UpdateTechDTO } from '../../../types/dto';

export class IndexedDBTechRepository implements ITechRepository {
  async getAll(): Promise<Tech[]> {
    return db.techs.toArray();
  }

  async getById(id: string): Promise<Tech | null> {
    const tech = await db.techs.get(id);
    return tech ?? null;
  }

  async getByUserId(userId: string): Promise<Tech | null> {
    const tech = await db.techs.where('userId').equals(userId).first();
    return tech ?? null;
  }

  async create(data: CreateTechDTO): Promise<Tech> {
    const tech: Tech = {
      id: crypto.randomUUID(),
      name: data.name,
      tier: data.tier,
      region: data.region,
      location: data.location,
      skills: data.skills,
      currentLoad: 0,
      maxLoad: data.maxLoad ?? 5,
      isAvailable: true,
      userId: data.userId,
      workStatus: data.workStatus ?? TechWorkStatusEnum.AVAILABLE,
    };

    await db.techs.add(tech);
    return tech;
  }

  async update(id: string, data: UpdateTechDTO): Promise<Tech | null> {
    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    // Convert null values to undefined for Tech compatibility
    const { currentTicketId, ...rest } = data;
    const updatedTech: Tech = {
      ...existing,
      ...rest,
      // Handle currentTicketId: null -> undefined conversion
      ...(currentTicketId !== undefined && {
        currentTicketId: currentTicketId ?? undefined,
      }),
    };

    await db.techs.put(updatedTech);
    return updatedTech;
  }

  async delete(id: string): Promise<boolean> {
    const existing = await this.getById(id);
    if (!existing) {
      return false;
    }
    await db.techs.delete(id);
    return true;
  }

  async getAvailable(): Promise<Tech[]> {
    const all = await this.getAll();
    return all.filter((t) => t.isAvailable && t.currentLoad < t.maxLoad);
  }

  async getByTier(tier: TechTier): Promise<Tech[]> {
    return db.techs.where('tier').equals(tier).toArray();
  }

  async getByRegion(region: Region): Promise<Tech[]> {
    return db.techs.where('region').equals(region).toArray();
  }

  async getByLocation(location: Location): Promise<Tech[]> {
    return db.techs.where('location').equals(location).toArray();
  }

  async getByWorkStatus(workStatus: TechWorkStatus): Promise<Tech[]> {
    return db.techs.where('workStatus').equals(workStatus).toArray();
  }

  async updateWorkStatus(id: string, workStatus: TechWorkStatus): Promise<Tech | null> {
    const isWorking = workStatus === TechWorkStatusEnum.WORKING;
    const isAvailable =
      workStatus === TechWorkStatusEnum.AVAILABLE || workStatus === TechWorkStatusEnum.WORKING;

    return this.update(id, {
      workStatus,
      isAvailable,
      workStartTime: isWorking ? new Date() : undefined,
    });
  }

  async updateCurrentTicket(id: string, ticketId: string | null): Promise<Tech | null> {
    const tech = await this.getById(id);
    if (!tech) {
      return null;
    }

    const wasWorking = tech.currentTicketId !== undefined;
    const isNowWorking = ticketId !== null;

    let newLoad = tech.currentLoad;
    if (!wasWorking && isNowWorking) {
      newLoad = Math.min(tech.currentLoad + 1, tech.maxLoad);
    }

    return this.update(id, {
      currentTicketId: ticketId ?? undefined,
      currentLoad: newLoad,
      workStatus: isNowWorking ? TechWorkStatusEnum.WORKING : TechWorkStatusEnum.AVAILABLE,
      workStartTime: isNowWorking ? new Date() : undefined,
    });
  }

  async incrementLoad(id: string): Promise<Tech | null> {
    const tech = await this.getById(id);
    if (!tech) {
      return null;
    }

    const newLoad = Math.min(tech.currentLoad + 1, tech.maxLoad);
    return this.update(id, { currentLoad: newLoad });
  }

  async decrementLoad(id: string): Promise<Tech | null> {
    const tech = await this.getById(id);
    if (!tech) {
      return null;
    }

    const newLoad = Math.max(tech.currentLoad - 1, 0);
    return this.update(id, { currentLoad: newLoad });
  }

  async updateAvailability(id: string, isAvailable: boolean): Promise<Tech | null> {
    return this.update(id, { isAvailable });
  }
}
